import { Dispatch } from '@reduxjs/toolkit';
import { ApiConstants } from '../constants/ApiConstants';
import axiosInstance from '../initializers/axios';
import IPermission from '../interfaces/IPermission';
import IUser from '../interfaces/IUser';
import IUserMenu from '../interfaces/IUserMenu';
import { SetEnums } from '../store/stores/allEnums/allEnums.store';

let enumsRequest: Promise<void> | null = null;
let lastEnumsUserKey: string | null = null;

const getUserKey = (currentUser: IUser | null) => {
  if (!currentUser) {
    return null;
  }

  return `${currentUser.id ?? '0'}:${currentUser.userName ?? ''}`;
};

const hasEnumValues = (enums: Record<string, any> | null | undefined) => {
  if (!enums || typeof enums !== 'object') {
    return false;
  }

  return Object.keys(enums).some((key) => key !== 'password');
};

export const ensureEnumsLoaded = async (dispatch: Dispatch<any>, currentUser: IUser | null, enums: Record<string, any> | null | undefined) => {
  const userKey = getUserKey(currentUser);
  if (!userKey) {
    lastEnumsUserKey = null;
    return;
  }

  if (hasEnumValues(enums)) {
    lastEnumsUserKey = null;
    return;
  }

  if (lastEnumsUserKey === userKey) {
    return;
  }

  if (!enumsRequest) {
    lastEnumsUserKey = userKey;
    enumsRequest = axiosInstance
      .get(ApiConstants.enum.allEnums)
      .then((response) => {
        if (hasEnumValues(response?.data)) {
          dispatch(SetEnums(response.data));
        }
      })
      .catch((error) => {
        console.error('Failed to lazy-load enums:', error);
      })
      .finally(() => {
        enumsRequest = null;
      });
  }

  await enumsRequest;
};

/**
 * Menu and permissions come from the Platform authorization snapshot, which
 * `app/LegacyRuntime.tsx` translates into the legacy store. The TechPOS endpoint this used to
 * call (`/api/system/user/info`) does not exist on PlatformAPI: calling it answered 404 for
 * every role without API-kind grants. Kept as a no-op so the ported hooks compile unchanged.
 */
export const ensureUserAccessLoaded = async (
  _dispatch: Dispatch<any>,
  _currentUser: IUser | null,
  _menu: IUserMenu[] | null | undefined,
  _permissions: IPermission[] | null | undefined,
): Promise<void> => {
  void _dispatch; void _currentUser; void _menu; void _permissions;
};
